var identity = function identity(x) {
  return x;
};
var ofIdentity = [identity];
var getNewIdSelector = function getNewIdSelector(fns) {
  var res = function res() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return fns.map(function (fn) {
      return encodeURIComponent(fn.apply(void 0, args));
    }).join('/');
  };
  res.fns = fns;
  res.idSelector = res;
  return res;
};
var combinedIdSelectors = {};
var getCombinedIdSelector = function getCombinedIdSelector(idSelectors) {
  var len = idSelectors.length;
  if (!combinedIdSelectors[len]) combinedIdSelectors[len] = [];
  var entry = combinedIdSelectors[len].find(function (_ref) {
    var candidates = _ref[0];
    return candidates.every(function (fn, idx) {
      return idSelectors[idx] === fn;
    });
  });
  if (entry) return entry[1];
  var fn = getNewIdSelector(idSelectors);
  combinedIdSelectors[len].push([idSelectors, fn]);
  return fn;
};
var getIdSelector = function getIdSelector(dependencies) {
  var sortedIdSelectors = dependencies.map(function (d) {
    return d.idSelector;
  }).filter(Boolean).sort();
  var uniqIdSelectors = [];
  var prevIdSelector;
  sortedIdSelectors.forEach(function (idSelector) {
    if (idSelector !== prevIdSelector) uniqIdSelectors.push(idSelector);
    prevIdSelector = idSelector;
  });
  if (uniqIdSelectors.length === 0) return null;
  return uniqIdSelectors.length === 1 ? uniqIdSelectors[0] : getCombinedIdSelector(uniqIdSelectors);
};
var getComputeFn = function getComputeFn(dependencies_, computeFn, idSelector, getCache) {
  var dependencies = dependencies_.length > 0 ? dependencies_ : ofIdentity;
  var nComputations = 0;
  if (!getCache) {
    var cache = new Array(2);
    getCache = function getCache() {
      return cache;
    };
  }
  var computeFnCached = function computeFnCached(computeFnArgs, id) {
    var cache = getCache(id);
    var prevArgs = cache[0],
      prevRes = cache[1];
    if (prevArgs && computeFnArgs.every(function (val, idx) {
      return val === prevArgs[idx];
    })) {
      return prevRes;
    }
    nComputations++;
    var res = computeFn.apply(void 0, computeFnArgs);
    cache[0] = computeFnArgs;
    cache[1] = res;
    return res;
  };
  var resFn = idSelector ? function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return computeFnCached(dependencies.map(function (fn) {
      return fn.apply(void 0, args);
    }), idSelector.apply(void 0, args));
  } : function () {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    return computeFnCached(dependencies.map(function (fn) {
      return fn.apply(void 0, args);
    }));
  };
  resFn.recomputations = function () {
    return nComputations;
  };
  resFn.resetRecomputations = function () {
    return nComputations = 0;
  };
  resFn.dependencies = dependencies;
  resFn.resultFunc = computeFn;
  resFn.resultFuncCached = computeFnCached;
  resFn.resultFuncCached.recomputations = resFn.recomputations;
  return resFn;
};
var getInstanceSelector = function getInstanceSelector(dependencies, computeFn, idSelector, cache) {
  if (cache === void 0) {
    cache = new Map();
  }
  var usages = new Map();
  var result = getComputeFn(dependencies, computeFn, idSelector, function (id) {
    if (!cache.has(id)) cache.set(id, new Array(2));
    return cache.get(id);
  });
  result.idSelector = idSelector;
  var inc = function inc(id) {
    return usages.set(id, (usages.get(id) || 0) + 1);
  };
  var dec = function dec(id) {
    var count = usages.get(id);
    if (count === undefined) return;
    if (count === 1) {
      cache.delete(id);
      usages.delete(id);
    } else {
      usages.set(id, count - 1);
    }
  };
  var usableDependencies = dependencies.filter(function (d) {
    return d.use;
  });
  result.use = function (id) {
    inc(id);
    var dependantUsages;
    if (idSelector.fns) {
      var ids = id.split('/').map(decodeURIComponent);
      dependantUsages = usableDependencies.map(function (x) {
        return x.use(ids[idSelector.fns.indexOf(x.idSelector)]);
      });
    } else {
      dependantUsages = usableDependencies.map(function (x) {
        return x.use(id);
      });
    }
    return function () {
      dec(id);
      dependantUsages.forEach(function (stop) {
        return stop();
      });
    };
  };
  result.clearCache = function (recursive) {
    if (recursive === void 0) {
      recursive = true;
    }
    cache.clear();
    usages.clear();
    if (recursive) usableDependencies.forEach(function (x) {
      return x.clearCache();
    });
  };
  return result;
};
var getDependencies = function getDependencies(args) {
  return args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
};
var createSelectorCreator = function createSelectorCreator(computeFnEnhancer) {
  return function () {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    var computeFn = computeFnEnhancer(args.splice(-1)[0]);
    var dependencies = getDependencies(args);
    var idSelector = getIdSelector(dependencies);
    var getSelector = idSelector ? getInstanceSelector : getComputeFn;
    return getSelector(dependencies, computeFn, idSelector);
  };
};
var createSelector = createSelectorCreator(identity);
var createStructuredSelector = function createStructuredSelector(obj) {
  var ids = Object.ids(obj);
  var compute = function compute() {
    var res = {};
    for (var _len = arguments.length, vals = new Array(_len), _key = 0; _key < _len; _key++) {
      vals[_key] = arguments[_key];
    }
    vals.forEach(function (val, idx) {
      return res[ids[idx]] = val;
    });
    return res;
  };
  return createSelector(Object.values(obj), compute);
};
var hasOwn = Object.prototype.hasOwnProperty;
function shallowEqual(objA, objB) {
  if (objA === objB) return true;
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }
  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (var i = 0; i < keysA.length; i++) {
    if (!hasOwn.call(objB, keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      return false;
    }
  }
  return true;
}
var createCollectionSelector = createSelectorCreator(function (computeFn) {
  var prevVal;
  return function () {
    var res = computeFn.apply(void 0, arguments);
    if (!shallowEqual(res, prevVal)) prevVal = res;
    return prevVal;
  };
});
var createIdSelector = function createIdSelector(fn) {
  var res = function res(s) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return fn.apply(void 0, args);
  };
  res.idSelector = res;
  return res;
};
var isInstanceSelector = function isInstanceSelector(sel) {
  return sel.idSelector && sel.use;
};
export { createCollectionSelector, createIdSelector, createSelector, createStructuredSelector, isInstanceSelector };